import { isDesktop } from '@global/helpers/general';
import { CameraAlt, CameraswitchOutlined, InfoOutlined } from '@mui/icons-material';
import { Button, Dialog, Grid, Typography } from '@mui/material';
import { RootState } from '@redux/store';
import React, { Dispatch, RefObject, SetStateAction, useEffect, useRef, useState } from 'react';
import { Camera } from 'react-camera-pro';
import { AspectRatio } from 'react-camera-pro/dist/components/Camera/types';
import { useSelector } from 'react-redux';
import { GeoAndIP, useGeoAndIP } from './helpers';

interface WebcamProps {
  setImage: Dispatch<SetStateAction<string>>;
  buttonRef: RefObject<HTMLButtonElement>;
  flipButtonRef: RefObject<HTMLButtonElement>;
  aspectRatio: AspectRatio;
  setOpenDialog: Dispatch<SetStateAction<boolean>>;
  acceptGeolocation?: boolean;
  setGeolocation: Dispatch<SetStateAction<GeoAndIP | null>>;
}

const WebcamCapture = (props: WebcamProps) => {
  const {
    setImage,
    buttonRef,
    flipButtonRef,
    aspectRatio,
    setOpenDialog,
    acceptGeolocation,
    setGeolocation
  } = props;
  const camViewRef = useRef(null);
  const { getCoords, startTracking, stopTracking } = useGeoAndIP();
  const [openNoCamDetectedDialog, setOpenNoCamDetectedDialog] = useState(false);
  const { currentVerification } = useSelector((s: RootState) => s.misc);
  const webcamRef = React.useRef<{ takePhoto: any, switchCamera: any }>(null);
  const capture = React.useCallback(async () => {
    try {
      const imageSrc = webcamRef.current?.takePhoto();
      if (acceptGeolocation) {
        const geolocation = await getCoords(currentVerification);
        if (geolocation.error) {
          return null;
        }
        setGeolocation(geolocation);
      }
      setImage(imageSrc);
    } catch (error: any) {
      setOpenNoCamDetectedDialog(error?.message);
    }
  }, [setImage, webcamRef, currentVerification]);

  useEffect(() => {
    if (acceptGeolocation) {
      startTracking();
      return () => {
        stopTracking();
      };
    }
  }, [acceptGeolocation]);

  return (
    <Grid
      container
      direction="column"
      justifyContent="flex-start"
      alignItems="center"
      sx={{
        padding: '1em',
        width: isDesktop() ? '33vw' : '100vw',
        // maxWidth: `${MAX_WIDTH}px`,
        background: '#000000',
        height: '100%',
        margin: 'auto'
      }}>
      <div
        ref={camViewRef}
        id="camera-container"
        style={{
          // maxWidth: `${MAX_WIDTH}px`,
          border: '3.5px dashed #FFFFFF',
          width: '100%',
          color: 'white'
        }}>
        <Camera
          ref={webcamRef}
          facingMode="user"
          aspectRatio={aspectRatio}
          errorMessages={{
            noCameraAccessible: 'No camera is accessible',
            permissionDenied: 'Please allow camera permission',
            switchCamera: "The camera can't be switched"
          }}
          //   numberOfCamerasCallback={(noOfCams) => {
          //   }}
        />
      </div>
      <Button
        ref={buttonRef}
        variant="outlined"
        color="secondary"
        sx={{
          display: 'none',
          padding: '1em 2em',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '45%',
          typography: 'body2'
          // maxWidth: MAX_WIDTH_HALF
        }}
        onClick={() => {
          capture();
        }}>
        <CameraAlt />
        &nbsp; Capture
      </Button>

      <Button
        ref={flipButtonRef}
        variant="outlined"
        sx={{
          display: 'none',
          padding: '1em 2em',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          position: 'absolute',
          top: '5em',
          right: '2em',
          width: '45%',
          typography: 'body2'
          // maxWidth: MAX_WIDTH_HALF
        }}
        onClick={() => {
          try {
            webcamRef.current?.switchCamera();
          } catch (error: any) {
            setOpenNoCamDetectedDialog(error?.message);
          }
        }}>
        <CameraswitchOutlined />
        &nbsp; Flip Camera
      </Button>

      <Dialog
        maxWidth="xs"
        fullWidth
        open={Boolean(openNoCamDetectedDialog)}
        onClose={() => {
          setOpenNoCamDetectedDialog(false);
        }}
        sx={{ padding: '1em' }}>
        <Grid
          container
          direction="column"
          justifyContent="center"
          alignItems="center"
          sx={{ padding: '1em' }}>
          <Typography
            variant="body1"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              textAlign: 'center'
            }}>
            <InfoOutlined color="primary" sx={{ fontSize: 40, marginBottom: '0.4em' }} />
            {openNoCamDetectedDialog}
          </Typography>

          <Grid
            container
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ padding: '1em', mt: '.5rem' }}>
            <Button
              variant="outlined"
              sx={{ width: '100%', padding: '1rem' }}
              onClick={async () => {
                setOpenDialog(false);
              }}>
              OK
            </Button>
          </Grid>
        </Grid>
      </Dialog>
    </Grid>
  );
};

export default WebcamCapture;
